import { useEffect } from 'react';

import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useTasksStoreActions } from './store';

type HookWithAssistanceServiceParams<P> = Omit<P, 'assistanceServiceInjectedParams'> &
	AssistanceServiceInputParam;

/**
 * Mounts an SSE subscription for the unified tasks store while the caller is mounted.
 *
 * - When `enabled` is true, increments the store's SSE subscriber count on mount,
 *   and decrements it on unmount or when `enabled` becomes false.
 * - The store opens the physical SSE connection when count goes from 0 → 1
 *   and closes it when it drops back to 0 (ref-counted, tab-visibility aware).
 *
 * Mirrors the pattern used by legacy `useInitTasksSSE` — wraps an inner hook with
 * `createHookWithAssistanceService` so the assistance service is injected by the
 * shared chat context, instead of relying on consumers to plumb a `getService`
 * through their own params.
 */
const useUnifiedTasksSSESubscriptionWithAssistanceService = ({
	enabled,
	assistanceServiceInjectedParams: { assistanceService },
}: {
	enabled: boolean;
} & AssistanceServiceInjectedParams): void => {
	const { mountSSESubscriber, unmountSSESubscriber } = useTasksStoreActions();

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}
		mountSSESubscriber(() => assistanceService);
		return () => unmountSSESubscriber();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, assistanceService]);
};

export const useUnifiedTasksSSESubscription: (
	props: HookWithAssistanceServiceParams<{ enabled: boolean } & AssistanceServiceInjectedParams>,
) => void = createHookWithAssistanceService(useUnifiedTasksSSESubscriptionWithAssistanceService);
